import { useMutation, useQueryClient } from '@tanstack/react-query';
import API, { getCompressedFile } from '../../../context/API';
import { notification } from '../../../commonComponents/Notification/Notification';
import { Category, Customization, Item, ItemFormValues } from '../types';
import { QUERY_KEYS } from '../constants';

interface SubmitItemPayload {
  formValues: ItemFormValues;
  imageFile: File | null;
  existingPicture: string;
  badgeImageFile: File | null;
  existingBadgeImage: string;
  selectedCustomizations: Customization[];
  selectedCategories: Category[];
  editingItem: Item | null;
}

async function buildItemFormData(
  formValues: ItemFormValues,
  imageFile: File | null,
  existingPicture: string,
  badgeImageFile: File | null,
  existingBadgeImage: string,
  selectedCustomizations: Customization[],
  selectedCategories: Category[],
  isUpdate: boolean,
): Promise<FormData> {
  const customizationsJson = JSON.stringify(
    selectedCustomizations.map((c) => ({
      customization: c.id,
      sequence_number: c.sequence_number,
    }))
  );
  const categoryNamesJson = JSON.stringify(
    selectedCategories.map((cat) =>
      typeof cat === 'object' && 'id' in cat ? cat.id.toString() : String(cat)
    )
  );
  const price = Number(parseFloat(formValues.price) || 0).toFixed(2);

  const formData = new FormData();
  formData.append('item', formValues.item);
  formData.append('name', formValues.item);
  formData.append('description', formValues.description || '');
  formData.append('price', price);
  // Boolean workaround: Boolean('false') === true in NestJS implicit conversion.
  // Send '' for false since Boolean('') === false, 'true' for true.
  formData.append('taxable', formValues.taxable ? 'true' : '');
  formData.append('is_make_it_a_meal', formValues.is_make_it_a_meal ? 'true' : 'false');
  formData.append('customizations', customizationsJson);
  formData.append('category_names', categoryNamesJson);

  if (imageFile) {
    // New file selected — upload it
    const compressed = await getCompressedFile(imageFile);
    formData.append('picture', compressed, imageFile.name);
  } else if (isUpdate && !existingPicture) {
    // Update + user deleted the image → clear it
    formData.append('picture', '');
  }

  if (badgeImageFile) {
    const compressed = await getCompressedFile(badgeImageFile);
    formData.append('badgeImage', compressed, badgeImageFile.name);
  } else if (isUpdate && !existingBadgeImage) {
    // Update + user deleted badge image → clear it (BE parseNullableStringField converts '' → null)
    formData.append('badgeImage', '');
  }

  return formData;
}

export function useSubmitItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      formValues,
      imageFile,
      existingPicture,
      badgeImageFile,
      existingBadgeImage,
      selectedCustomizations,
      selectedCategories,
      editingItem,
    }: SubmitItemPayload) => {
      const formData = await buildItemFormData(
        formValues,
        imageFile,
        existingPicture,
        badgeImageFile,
        existingBadgeImage,
        selectedCustomizations,
        selectedCategories,
        !!editingItem,
      );

      if (!editingItem) {
        await API.post('/items', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await API.put(`/items/${editingItem.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
    },
    onSuccess: (_, { editingItem }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ITEMS] });
      notification.success(
        editingItem ? 'Item updated' : 'Item created',
        `The item was successfully ${editingItem ? 'updated' : 'created'}.`,
        5
      );
    },
    onError: (error: any) => {
      let msg = error.response?.data?.message || error.response?.data || error.message || 'Unable to save the item.';
      if (msg.includes('value too long for type')) msg = 'The image file name is too long.';
      notification.error('An error occurred', msg, 5);
    },
  });
}

export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => API.delete(`/items/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ITEMS] });
      notification.success('Item deleted', 'The item was successfully deleted.', 5);
    },
    onError: () => {
      notification.error('An error occurred', 'Unable to delete the item.', 5);
    },
  });
}

export function useToggleSoldOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, sold_out }: { id: number | string; sold_out: boolean }) =>
      API.put(`/items/${id}`, { sold_out }),
    onMutate: async ({ id, sold_out }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.ITEMS] });
      const previousData = queryClient.getQueriesData({ queryKey: [QUERY_KEYS.ITEMS] });
      queryClient.setQueriesData({ queryKey: [QUERY_KEYS.ITEMS] }, (old: any) => {
        if (!old?.items) return old;
        return {
          ...old,
          items: old.items.map((i: Item) =>
            String(i.id) === String(id) ? { ...i, sold_out } : i
          ),
        };
      });
      return { previousData };
    },
    onSuccess: (_, { sold_out }) => {
      notification.success(
        'Status updated',
        sold_out
          ? 'Item marked as sold out and moved to the Sold Out tab.'
          : 'Item marked as available and moved back to the Items tab.',
        3
      );
    },
    onError: (_err, _vars, context: any) => {
      context?.previousData?.forEach(([queryKey, data]: any) => {
        queryClient.setQueryData(queryKey, data);
      });
      notification.error('Error', 'Failed to update sold out status.', 5);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ITEMS] });
    },
  });
}
